import { Row, Select, H5 } from '@fs/zion-ui'
import { usePageView } from '@fs/zion-analytics'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { usePageTitle } from '../../app/PageTitleContext'
import useNotifications from '../../../hooks/useNotifications'
import { getAllPersons, getCountries } from '../../../services/famousHistoricalService'
import { getMatchTerms } from '../../../services/sensitiveService'
import { downloadFile } from '../../../util/FileDownloadUtil'
import CreatePersonButton from './CreatePersonButton'
import DownloadCategoryButton from './DownloadCategoryButton'
import FamousSearch from './famous/FamousSearch'
import HistoricalSearch from './historical/HistoricalSearch'
import MatchTermSearch from './matchTerm/MatchTermSearch'

const Lists = () => {
  usePageView({ page_detail: 'DMConsole:Lists' })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.tools.lists'))
  const { error } = useNotifications()
  const categories = [
    {
      value: 'christian-religion-names',
      label: t('tools.lists.categories.christianReligiousNameIndicators', 'Christian Religious Name Indicators'),
    },
    { value: 'deity', label: t('deity', 'Deity') },
    { value: 'famous', label: t('famous', 'Famous') },
    { value: 'fictitious', label: t('fictitious', 'Fictitious') },
    { value: 'historical', label: t('historical', 'Historical') },
    {
      value: 'holocaust-contributor-ids',
      label: t('tools.lists.categories.highHolocaustContributor', 'High Holocaust Contributor'),
    },
    { value: 'mythological', label: t('mythological', 'Mythological') },
    {
      value: 'non-holocaust-countries',
      label: t('tools.lists.categories.nonHolocaustCountries', 'Non-Holocaust Countries'),
    },
    {
      value: 'primary-death-camp-names',
      label: t('tools.lists.categories.primaryHolocaustDeathCamps', 'Primary Holocaust Death Camps'),
    },
    { value: 'profanity', label: t('tools.lists.categories.profanity', 'Profanity') },
    { value: 'religious-titles', label: t('tools.lists.categories.religiousTitle', 'Religious Title') },
    {
      value: 'secondary-death-camp-names',
      label: t('tools.lists.categories.secondaryHolocaustDeathCamps', 'Secondary Holocaust Death Camps'),
    },
  ]
  const [category, setCategory] = useState()
  const langs = [
    { value: 'en', label: t('lang.english', 'English') },
    { value: 'pt', label: t('lang.portuguese', 'Portuguese') },
    { value: 'es', label: t('lang.spanish', 'Spanish') },
  ]
  const [lang, setLang] = useState(langs[0].value)
  const [countries, setCountries] = useState()

  const downloadMatchTermCategoryButtonOnClick = () => {
    getMatchTerms(category, lang)
      .then((response) => {
        const stringList = ['Id,Term']
        response.data.matchTerms.forEach((mt) => {
          stringList.push(`${mt.id},${mt.term}`)
        })
        const matchTermsString = stringList.join('\n')
        downloadFile(matchTermsString, `${category}-match-terms.csv`)
      })
      .catch((err) => {
        error(
          t('tools.errors.gettingMatchTerms', {
            defaultValue: `Error getting match terms for ${category}: ${err.message || ''}`,
            category,
            message: err.message || '',
          }),
          err
        )
      })
  }

  const getStringList = (allPersons) => {
    const stringList = []
    const famousColumns =
      'ID, Country, First Name, Surname, Category, Gender, Birth Date, Birth Place, Death Date, Death Place, Parent #1 Given Name, Parent #1 Surname, Parent #2 Given Name, Parent #2 Surname, Spouse Given Name, Spouse Surname, Biography'
    const historicalColumns =
      'ID, Country, Person ID, Given Name, Surname, Birth Date, Label, Locked, Needs Sources, Why Famous, Possible Duplicates'
    stringList.push([category === 'famous' ? famousColumns : historicalColumns])
    allPersons.data.personDtos.forEach((personDto) => {
      const person = JSON.parse(personDto.personJSON)
      let data
      if (category === 'historical') {
        data = `${personDto.id},${personDto.country},${person.pid},${person.givenName},${person.surname},${person.birthDate},${person.label},${person.locked},${person.needsSources},${person.reasonWhyFamous},${person.possibleDuplicates}`
      } else {
        data = `${personDto.id},${personDto.country},${person.firstName},${person.surname},${person.category},${person.gender},${person.birthDate},${person.birthPlace},${person.deathDate},${person.deathPlace},${person.parent1GivenName},${person.parent1Surname},${person.parent2GivenName},${person.parent2Surname},${person.spouseGivenName},${person.spouseSurname},${person.biography}`
      }
      stringList.push(data)
    })
    return stringList
  }

  const downloadFamousCategoryOnClick = async () => {
    try {
      const allPersons = await getAllPersons('famous')
      const stringList = getStringList(allPersons)
      const matchTermsString = stringList.join('\n')
      downloadFile(matchTermsString, 'famous-persons.csv')
    } catch (err) {
      // Notify admin of error
    }
  }

  const downloadHistoricalCategoryOnClick = async () => {
    try {
      const allPersons = await getAllPersons('historical')
      const stringList = getStringList(allPersons)
      const matchTermsString = stringList.join('\n')
      downloadFile(matchTermsString, 'historical-persons.csv')
    } catch (err) {
      // Notify admin of error
    }
  }

  useEffect(() => {
    let isCurrent = true
    if (countries === undefined && (category === 'famous' || category === 'historical')) {
      getCountries()
        .then((response) => {
          if (isCurrent) {
            setCountries(response.data)
          }
        })
        .catch((err) => {
          error(
            `${t('tools.lists.notifications.getCountriesError', 'Error retrieving countries')}: ${err.message}`,
            err
          )
        })
    }
    return () => {
      isCurrent = false
    }
  }, [category, countries, error, t])

  let searchUI = null
  let actions = null
  let downloadCategoryButtonOnClick = null
  switch (category) {
    case 'famous':
      searchUI = <FamousSearch countries={countries} />
      actions = <CreatePersonButton category={category} countries={countries} alignY="nextToInput" />
      downloadCategoryButtonOnClick = downloadFamousCategoryOnClick
      break
    case 'historical':
      searchUI = <HistoricalSearch countries={countries} />
      actions = <CreatePersonButton category={category} countries={countries} alignY="nextToInput" />
      downloadCategoryButtonOnClick = downloadHistoricalCategoryOnClick
      break
    case 'christian-religion-names':
    case 'deity':
    case 'fictitious':
    case 'holocaust-contributor-ids':
    case 'mythological':
    case 'non-holocaust-countries':
    case 'primary-death-camp-names':
    case 'profanity':
    case 'religious-titles':
    case 'secondary-death-camp-names':
      searchUI = <MatchTermSearch category={category} lang={lang} setLang={setLang} langs={langs} />
      downloadCategoryButtonOnClick = downloadMatchTermCategoryButtonOnClick
      break
    default:
      break
  }

  return (
    <>
      <Row guttersY="none">
        <H5>{t('tools.lists.headings.main', 'Manage Data Quality Lists')}</H5>
      </Row>
      <Row guttersY="none">
        <Select
          id="category"
          placeholder={t('tools.lists.placeholders.selectCategory', 'Select Category')}
          label={t('category', 'Category')}
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          options={categories}
        />
        {category && <DownloadCategoryButton growX alignY="middle" onClick={downloadCategoryButtonOnClick} />}
        {actions}
      </Row>
      {searchUI}
    </>
  )
}

export default Lists
